import { useEffect, useState } from "react";
import { useParams, Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { ShoppingBag, ArrowLeft, Loader2, ExternalLink, Trophy } from "lucide-react";
import { API } from "../lib/api";
import { InlineVideoEmbed } from "../components/InlineVideoEmbed";
import { StorefrontBuyModal } from "../components/commerce/StorefrontBuyModal";
import ProductThumb from "../components/commerce/ProductThumb";
import { externalLinkClickHandler } from "../lib/openExternal";
import { safeBackPath } from "../lib/backTarget";

/**
 * SellPage — v109. Focused single-product page at /p/{product_id}. This
 * is the "Linkola-generated sell URL" creators can copy from the
 * Products tab and share on socials to promote one specific product.
 * Reuses the same `StorefrontBuyModal` so checkout logic stays in one
 * place.
 *
 * Extracted from `pages/Storefront.jsx` during the v142.3 refactor.
 * Behaviour is unchanged; only the file location changed.
 */
export default function SellPage() {
  const { product_id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showBuy, setShowBuy] = useState(false);
  const [autoCoupon, setAutoCoupon] = useState("");

  useEffect(() => {
    let alive = true;
    fetch(`${API}/public/products/${encodeURIComponent(product_id)}/detail`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setError("Product not available"); });
    return () => { alive = false; };
  }, [product_id]);

  // Set OG meta tags for share previews.
  useEffect(() => {
    if (!data) return;
    const p = data.product; const c = data.creator;
    const title = `${p.title} — from ${c.name} · Linkola`;
    const desc = p.description || `Buy ${p.title} for ₹${p.price} directly from ${c.name}.`;
    // v109 — Social crawlers (FB / Twitter / LinkedIn) require absolute
    // URLs on `og:image` — a relative `/api/uploads/…` returns no
    // preview. Prefix the origin when the URL starts with `/`.
    const absolute = (u) => (!u ? "" : /^https?:\/\//i.test(u) ? u : `${window.location.origin}${u.startsWith("/") ? "" : "/"}${u}`);
    const ogImage = absolute(p.image_url) || absolute(c.picture) || "";
    document.title = title;
    const upsert = (attr, key, value) => {
      if (!value) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };
    upsert("property", "og:title", title);
    upsert("property", "og:description", desc);
    upsert("property", "og:image", ogImage);
    upsert("property", "og:url", `${window.location.origin}/p/${product_id}`);
    upsert("name", "twitter:card", ogImage ? "summary_large_image" : "summary");
    upsert("name", "twitter:title", title);
    upsert("name", "twitter:description", desc);
    upsert("name", "twitter:image", ogImage);
    upsert("name", "description", desc);
  }, [data, product_id]);

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-6" data-testid="sell-page-error">
        <div className="max-w-md text-center border-2 border-ink bg-white p-6 shadow-brutalSm">
          <p className="overline">Product</p>
          <h1 className="font-display font-black text-2xl mt-2">{error}</h1>
          <Link to="/" className="mt-4 inline-block border-2 border-ink bg-coral text-white px-3 py-1.5 font-bold text-sm shadow-brutalSm">
            Back home
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center" data-testid="sell-page-loading">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }
  const { product, creator, deals } = data;
  const canBuy = String(product.currency || "INR").toUpperCase() === "INR"
                 && Number(product.price) > 0;
  const isService = String(product.kind || "product").toLowerCase() === "service";
  // v146.98 — Suppress the "See more from {creator.name}" storefront
  // link + the "Visit product page" external link when the SellPage is
  // opened via an affiliate share link (`?ref=` or `?pick=`). Both
  // links would send the buyer to the brand's own surface, where the
  // affiliate ref is lost and the creator loses their commission on
  // any subsequent purchase.
  const isAffiliateReferral = typeof window !== "undefined"
    && (new URLSearchParams(window.location.search).has("ref")
        || new URLSearchParams(window.location.search).has("pick"));
  const openBuyWithCode = (code) => { setAutoCoupon(code || ""); setShowBuy(true); };

  // v149.6 — Back-target resolution. `backKind` is mirrored onto the
  // button as a data attribute so the behaviour is assertable without
  // having to actually navigate.
  const backParam = safeBackPath(searchParams.get("back"));
  // `location.key === "default"` means this is the first entry of the
  // router session — the visitor landed here directly, so there is no
  // in-app page to pop back to.
  //
  // NOT `window.history.length > 1`: that counts the whole TAB's session,
  // including the new-tab `about:blank` and any site the visitor was on
  // before us. Measured: a tab opened straight onto this route already
  // reports length 2, so that check sent Back to about:blank — and from
  // an Instagram or Google referral it would have walked the visitor
  // straight off Linkola.
  const hasAppHistory = location.key !== "default";
  const backKind = backParam ? "origin"
    : hasAppHistory ? "history"
      : creator.slug ? "seller"
        : "home";
  const backLabel = backKind === "seller"
    ? `Back to ${creator.name || "the seller"}`
    : backKind === "home" ? "Home" : "Back";
  const goBack = () => {
    if (backParam) { navigate(backParam); return; }
    if (hasAppHistory) { navigate(-1); return; }
    if (creator.slug) { navigate(`/u/${creator.slug}`); return; }
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="sell-page">
      <header className="border-b-2 border-ink bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-3">
          {/* v149.6 — Was a hardcoded `<Link to="/">`: whoever tapped this
              arrow landed on the Linkola marketing page, never back where
              they came from. The common way in is a Quick Promote product
              ad on someone else's bio page, which opens this route with
              `target="_blank" rel="noreferrer"` — so the new tab has no
              history, no referrer and no opener, and the origin can only
              come from `?back=`. Order: explicit origin → real history
              (same-tab arrivals) → the seller's own bio hub → home. */}
          <button
            type="button"
            onClick={goBack}
            className="border-2 border-ink bg-white p-1.5 shadow-brutalSm"
            aria-label={backLabel}
            title={backLabel}
            data-testid="sell-page-back"
            data-back-kind={backKind}
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
          {creator.picture ? (
            <img src={creator.picture} alt="" className="w-10 h-10 rounded-full border-2 border-ink object-cover" loading="eager" decoding="async" />
          ) : (
            <div className="w-10 h-10 rounded-full border-2 border-ink bg-coral text-white flex items-center justify-center font-black">
              {(creator.name || "?").charAt(0).toUpperCase()}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-bold uppercase tracking-wider text-ink/60">{isService ? "Service" : "Product"}</p>
            <p className="font-bold text-sm truncate" data-testid="sell-page-creator-name">
              {isAffiliateReferral ? "Recommended for you" : `from ${creator.name}`}
            </p>
          </div>
        </div>
      </header>
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="grid md:grid-cols-2 gap-6 items-start">
          <div className="border-2 border-ink bg-white shadow-brutal aspect-square overflow-hidden">
            <ProductThumb src={product.image_url} name={product.title} alt={product.title}
              className="w-full h-full" loading="eager" fetchPriority="high" fallbackTestid="sell-page-initials" />
          </div>
          <div className="space-y-4">
            <div>
              <p className="overline">{isService ? "Book directly" : "Buy directly"}</p>
              <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter leading-tight" data-testid="sell-page-title">
                {product.title}
                {product.is_best_seller && (
                  <span className="ml-2 align-middle text-[10px] font-bold border border-ink px-1.5 py-0.5 bg-sun inline-flex items-center gap-1" data-testid="sell-page-bestseller-badge">
                    <Trophy className="w-2.5 h-2.5" strokeWidth={3} /> BEST SELLER
                  </span>
                )}
                {isService && (
                  <span className="ml-2 align-middle text-[10px] font-bold border border-ink px-1.5 py-0.5 bg-lavender" data-testid="sell-page-service-badge">SERVICE</span>
                )}
              </h1>
            </div>
            <p className="font-display font-black text-4xl" data-testid="sell-page-price">
              ₹{Number(product.price || 0).toLocaleString("en-IN")}
              <span className="text-xs text-ink/50 font-bold ml-2">
                {product.currency || "INR"}
              </span>
            </p>
            {product.description && (
              <p className="text-sm text-ink/80 whitespace-pre-wrap" data-testid="sell-page-desc">
                {product.description}
              </p>
            )}
            {product.instructions && (
              <div className="border-2 border-ink bg-white p-3 shadow-brutalSm" data-testid="sell-page-instructions">
                <p className="overline mb-1">{isService ? "Booking terms & instructions" : "Instructions & terms"}</p>
                <p className="text-xs text-ink/80 whitespace-pre-wrap">{product.instructions}</p>
              </div>
            )}
            {product.social_link && (
              <div className="border-2 border-ink shadow-brutalSm bg-black" data-testid="sell-page-reel-wrap">
                <InlineVideoEmbed
                  url={product.social_link}
                  testid="sell-page-reel-embed"
                  productId={product.product_id}
                  surface="sell_page"
                />
              </div>
            )}
            {canBuy ? (
              <button
                type="button"
                onClick={() => openBuyWithCode("")}
                data-testid="sell-page-buy-btn"
                className="w-full border-2 border-ink bg-coral text-white px-4 py-3 font-bold text-base shadow-brutal hover:translate-y-[-1px] transition-transform inline-flex items-center justify-center gap-2"
              >
                <ShoppingBag className="w-4 h-4" /> {isService ? "Book" : "Buy"} for ₹{Number(product.price || 0).toLocaleString("en-IN")}
              </button>
            ) : product.buy_url && !isAffiliateReferral ? (
              <a
                href={product.buy_url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={externalLinkClickHandler(product.buy_url)}
                data-testid="sell-page-external-btn"
                className="w-full border-2 border-ink bg-ink text-white px-4 py-3 font-bold text-base shadow-brutal inline-flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" /> {isService ? "Visit booking page" : "Visit product page"}
              </a>
            ) : null}
            {creator.slug && !isAffiliateReferral && (
              <Link
                to={`/store/${creator.slug}`}
                data-testid="sell-page-storefront-link"
                className="w-full border-2 border-ink bg-white px-4 py-2 font-bold text-sm shadow-brutalSm inline-flex items-center justify-center gap-2"
              >
                <ShoppingBag className="w-3 h-3" /> See more from {creator.name}
              </Link>
            )}
          </div>
        </div>
        {deals && deals.length > 0 && canBuy && (
          <section className="mt-8" data-testid="sell-page-deals">
            <p className="overline mb-2">Available deals — tap to apply</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {deals.map((d, i) => (
                <button
                  key={d.code}
                  type="button"
                  onClick={() => openBuyWithCode(d.code)}
                  data-testid={`sell-page-deal-${i}`}
                  className="border-2 border-ink bg-mint px-3 py-2 shadow-brutalSm text-left hover:translate-y-[-1px] transition-transform"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-display font-black text-2xl leading-none">{d.discount_pct}%</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider">off</span>
                  </div>
                  <code className="mt-1 inline-block font-mono bg-white border-2 border-ink px-1.5 py-0.5 text-[11px]">
                    {d.code}
                  </code>
                  {d.description && (
                    <p className="mt-1 text-[10px] text-ink/70 line-clamp-2">{d.description}</p>
                  )}
                  {d.min_purchase_inr && (
                    <p className="text-[10px] text-ink/60">Min cart ₹{Number(d.min_purchase_inr).toFixed(0)}</p>
                  )}
                </button>
              ))}
            </div>
          </section>
        )}
      </main>
      <footer className="max-w-4xl mx-auto px-4 sm:px-6 pt-4 pb-10 text-center">
        <p className="text-xs text-ink/50">
          Powered by <span className="font-bold">Linkola</span> · Secure checkout via Razorpay
        </p>
      </footer>
      {showBuy && (
        <StorefrontBuyModal
          product={product}
          slug={creator.slug || "product"}
          requireOtp={!!data.settings?.require_phone_otp}
          otpChannel={data.settings?.otp_channel || "auto"}
          codEnabled={!!data.settings?.cod_enabled}
          onlineEnabled={data.settings?.online_enabled !== false}
          initialCouponCode={autoCoupon}
          onClose={() => { setShowBuy(false); setAutoCoupon(""); }}
          onSuccess={() => { setShowBuy(false); setAutoCoupon(""); }}
        />
      )}
    </div>
  );
}
